/**
 * Demo Flow Service
 *
 * Config-driven step definitions for the guided demo walkthrough.
 * Each step defines a target element (CSS selector), route, tooltip text,
 * and optional action (auto-click, highlight, etc.).
 *
 * The flow is configurable per role (CC vs CCM).
 */

export type DemoStepAction = 'highlight' | 'click' | 'navigate' | 'scroll';

export interface DemoStep {
  id: string;
  title: string;
  description: string;
  /** CSS selector for the target element to highlight. Null = full-screen overlay. */
  targetSelector: string | null;
  /** Route to navigate to before highlighting. Null = stay on current route. */
  route: string | null;
  /** Action to perform on the target. */
  action: DemoStepAction;
  /** Badge label shown on the step indicator. */
  badge?: string;
  /** Delay in ms before auto-advancing to next step. 0 = manual. */
  autoAdvanceMs?: number;
}

export interface DemoFlow {
  id: string;
  name: string;
  description: string;
  role: 'CC' | 'CCM' | 'ALL';
  steps: DemoStep[];
}

// ─── CC FLOW ──────────────────────────────────────────────

const CC_STEPS: DemoStep[] = [
  {
    id: 'cc-1',
    title: 'Welcome to the Workspace',
    description: 'This is your home base. The workspace shows KPIs, patient lists, and quick actions tailored for your role.',
    targetSelector: null,
    route: '/programs/:programId/workspace',
    action: 'navigate',
    badge: 'Workspace',
  },
  {
    id: 'cc-2',
    title: 'KPI Summary Strip',
    description: 'These KPI cards show real-time metrics: total patients, active cases, pending approvals, and call log stats.',
    targetSelector: '[data-demo="kpi-strip"]',
    route: null,
    action: 'highlight',
    badge: 'KPIs',
  },
  {
    id: 'cc-3',
    title: 'Patient List',
    description: 'Browse and search patients. Click any patient row to open their full profile (Patient 360).',
    targetSelector: '[data-demo="patient-table"]',
    route: null,
    action: 'highlight',
    badge: 'Patients',
  },
  {
    id: 'cc-4',
    title: 'Create a New Patient',
    description: 'Click "Create Patient" to open the dynamic enrollment form. All fields come from the published form template.',
    targetSelector: '[data-demo="create-patient-btn"]',
    route: null,
    action: 'highlight',
    badge: 'Create',
  },
  {
    id: 'cc-5',
    title: 'Patient Details View',
    description: 'The Patient Details page shows demographics, case history, treatment timeline, call logs, and documents — all in one place.',
    targetSelector: null,
    route: '/programs/:programId/patients/:patientId',
    action: 'navigate',
    badge: 'Patient Details',
  },
  {
    id: 'cc-6',
    title: 'Create Assistance Case',
    description: 'From the Patient 360, click "Create Case" to enroll the patient in an assistance program. The form is driven by the published case template.',
    targetSelector: '[data-demo="create-case-btn"]',
    route: null,
    action: 'highlight',
    badge: 'Case',
  },
  {
    id: 'cc-7',
    title: 'Add Call Log',
    description: 'Log patient calls directly from the timeline. Call logs track type, status, notes, and follow-up dates.',
    targetSelector: '[data-demo="add-call-log-btn"]',
    route: null,
    action: 'highlight',
    badge: 'Call Log',
  },
  {
    id: 'cc-8',
    title: 'Walkthrough Complete!',
    description: 'You\'ve completed the Care Coordinator walkthrough. You can now explore the workspace, create patients, manage cases, and log calls.',
    targetSelector: null,
    route: null,
    action: 'highlight',
    badge: 'Done',
  },
];

// ─── CCM FLOW ──────────────────────────────────────────────

const CCM_STEPS: DemoStep[] = [
  {
    id: 'ccm-1',
    title: 'Welcome to the Management Dashboard',
    description: 'As a Care Case Manager, your dashboard shows program-wide KPIs, approval queues, and team performance.',
    targetSelector: null,
    route: '/programs/:programId/workspace',
    action: 'navigate',
    badge: 'Dashboard',
  },
  {
    id: 'ccm-2',
    title: 'Approval Queue',
    description: 'Cases awaiting your approval are highlighted. Review case details, then approve or reject with required comments.',
    targetSelector: '[data-demo="approval-queue"]',
    route: null,
    action: 'highlight',
    badge: 'Approvals',
  },
  {
    id: 'ccm-3',
    title: 'Review a Case',
    description: 'Click any pending case to open the full case detail. Review patient history, treatment plan, and financial impact before deciding.',
    targetSelector: '[data-demo="case-detail"]',
    route: null,
    action: 'highlight',
    badge: 'Review',
  },
  {
    id: 'ccm-4',
    title: 'Approve or Reject',
    description: 'Use the action buttons to approve or reject. A comment is required for all decisions — this creates an audit trail.',
    targetSelector: '[data-demo="approve-reject-btns"]',
    route: null,
    action: 'highlight',
    badge: 'Decision',
  },
  {
    id: 'ccm-5',
    title: 'View Reports & Analytics',
    description: 'Navigate to Reports to see utilization trends, approval turnaround, and financial summaries across the program.',
    targetSelector: null,
    route: '/programs/:programId/reports',
    action: 'navigate',
    badge: 'Reports',
  },
  {
    id: 'ccm-6',
    title: 'Dynamic Dashboard',
    description: 'The dynamic dashboard shows real-time widgets: case distributions, trend charts, and performance metrics.',
    targetSelector: '[data-demo="dynamic-dashboard"]',
    route: null,
    action: 'highlight',
    badge: 'Dashboard',
  },
  {
    id: 'ccm-7',
    title: 'Walkthrough Complete!',
    description: 'You\'ve completed the Care Case Manager walkthrough. You can manage approvals, monitor KPIs, and generate reports.',
    targetSelector: null,
    route: null,
    action: 'highlight',
    badge: 'Done',
  },
];

// ─── FLOW REGISTRY ──────────────────────────────────────────

export const DEMO_FLOWS: DemoFlow[] = [
  {
    id: 'cc-flow',
    name: 'Care Coordinator Walkthrough',
    description: 'Learn how to enroll patients, create cases, and log calls.',
    role: 'CC',
    steps: CC_STEPS,
  },
  {
    id: 'ccm-flow',
    name: 'Care Case Manager Walkthrough',
    description: 'Learn how to review cases, approve requests, and monitor dashboards.',
    role: 'CCM',
    steps: CCM_STEPS,
  },
];

// ─── HELPER FUNCTIONS ──────────────────────────────────────

export function isDemoModeEnabled(): boolean {
  return import.meta.env.VITE_DEMO_MODE === 'true';
}

// ─── PROGRAM-LEVEL DEMO GATE ───────────────────────────────
//
// Demo features only run when BOTH of these are true:
//   1. The global env flag (VITE_DEMO_MODE === 'true')
//   2. The current program's `IsDemoEnabled` flag (from /programs/{id})
//
// React components access the gate through the `useDemoAccess` hook.
// Non-React code (e.g. DemoNarrationService) reads the cached value
// pushed into this module by `setProgramDemoEnabled`.

const programDemoFlags = new Map<string, boolean>();
let activeProgramId: string | null = null;
const demoAccessListeners = new Set<() => void>();

/**
 * Push the per-program demo flag into the runtime cache.
 * Called by `useDemoAccess` after the program is fetched.
 */
export function setProgramDemoEnabled(programId: string | null, enabled: boolean): void {
  if (!programId) {
    activeProgramId = null;
    demoAccessListeners.forEach((fn) => fn());
    return;
  }
  programDemoFlags.set(programId, enabled);
  activeProgramId = programId;
  demoAccessListeners.forEach((fn) => fn());
}

/** Clear the cache (e.g. on logout / program switch). */
export function clearProgramDemoCache(): void {
  programDemoFlags.clear();
  activeProgramId = null;
  demoAccessListeners.forEach((fn) => fn());
}

/**
 * Returns true only if the env flag AND the program flag are both true.
 * If `programId` is omitted, the most recently set program is checked.
 * Returns false when nothing has been cached yet — fail-closed.
 */
export function isDemoAllowed(programId?: string | null): boolean {
  if (!isDemoModeEnabled()) return false;
  const id = programId ?? activeProgramId;
  if (!id) return false;
  return programDemoFlags.get(id) === true;
}

/** Subscribe to changes in the demo-access cache. Returns an unsubscribe fn. */
export function subscribeDemoAccess(listener: () => void): () => void {
  demoAccessListeners.add(listener);
  return () => demoAccessListeners.delete(listener);
}

export function getDemoFlowForRole(roleName: string): DemoFlow | null {
  const normalized = roleName.toLowerCase();
  if (normalized.includes('care coordinator') && !normalized.includes('manager')) {
    return DEMO_FLOWS.find((f) => f.role === 'CC') ?? null;
  }
  if (normalized.includes('case manager') || normalized.includes('ccm')) {
    return DEMO_FLOWS.find((f) => f.role === 'CCM') ?? null;
  }
  return null;
}

export function resolveStepRoute(route: string | null, programId: string, patientId?: string): string | null {
  if (!route) return null;
  let resolved = route.replace(':programId', programId);
  if (patientId) resolved = resolved.replace(':patientId', patientId);
  return resolved;
}

const DEMO_STATE_KEY = 'epprem_demo_state';

interface DemoState {
  flowId: string;
  currentStepIndex: number;
  isActive: boolean;
  completedAt?: string;
}

export function saveDemoState(state: DemoState): void {
  localStorage.setItem(DEMO_STATE_KEY, JSON.stringify(state));
}

export function loadDemoState(): DemoState | null {
  const raw = localStorage.getItem(DEMO_STATE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as DemoState;
  } catch {
    return null;
  }
}

export function clearDemoState(): void {
  localStorage.removeItem(DEMO_STATE_KEY);
}
